/** @format — Document display helpers */

import type { ApiDocument, DocumentItem, DocumentUploadType } from "../types";

const PHOTO_TYPES = new Set(["photo", "image", "pictures", "picture"]);

export const DOCUMENT_TYPE_OPTIONS: Array<{ value: "all" | string; label: string }> = [
  { value: "all", label: "All types" },
  { value: "bol", label: "BOL" },
  { value: "telex", label: "TELEX" },
  { value: "telex_release", label: "TELEX RELEASE" },
  { value: "coi", label: "COI" },
  { value: "receipt", label: "RECEIPT" },
  { value: "invoice", label: "INVOICE" },
  { value: "photo", label: "PHOTO" },
  { value: "inv", label: "INV" },
];

export const DATE_FILTER_OPTIONS = [
  { value: "all", label: "All time" },
  { value: "30d", label: "Last 30 days" },
  { value: "3m", label: "Last 3 months" },
  { value: "6m", label: "Last 6 months" },
  { value: "custom", label: "Custom" },
] as const;

export const SORT_BY_OPTIONS = [
  { value: "date_created", label: "Date created" },
  { value: "listing", label: "Shipments" },
] as const;

export const SORT_ORDER_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
] as const;

export function formatUploadType(uploadType?: string | null): string {
  const raw = String(uploadType ?? "").trim();
  if (!raw) return "DOC";
  return raw.replace(/_/g, " ").toUpperCase();
}

export function formatFileSize(bytes?: number | null): string {
  const size = Number(bytes ?? 0);
  if (!Number.isFinite(size) || size <= 0) return "—";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatDateLabel(iso?: string | null): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function formatDateShort(iso?: string | null): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function titleCaseWords(text: string): string {
  return text
    .split(/([\s-]+)/)
    .map((part) => {
      if (/^[\s-]+$/.test(part)) return part;
      if (/^\d+$/.test(part)) return part;
      if (/^[A-Z0-9]{2,5}$/.test(part)) return part;
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join("");
}

function titleCaseNameSegment(segment: string): string {
  return segment
    .split("-")
    .map((part, index) => {
      if (index === 0) return part;
      return titleCaseWords(part);
    })
    .join("-");
}

function vehicleLabel(doc: ApiDocument): string {
  const vehicle = doc.listing?.vehicles?.[0];
  if (!vehicle) return "";
  const raw = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(" ").trim();
  return titleCaseWords(raw);
}

function buildDisplayName(doc: ApiDocument): string {
  const type = formatUploadType(doc.uploadType);
  const vehicle = vehicleLabel(doc);
  const created = formatDateShort(doc.createdAt);
  if (vehicle) return `${type}-${vehicle}-${created}`;
  if (doc.name?.trim()) return titleCaseNameSegment(doc.name.trim());
  if (doc.filename?.trim()) return titleCaseNameSegment(doc.filename.trim());
  return type;
}

export function isPhotoUploadType(uploadType?: DocumentUploadType | null): boolean {
  return PHOTO_TYPES.has(String(uploadType ?? "").toLowerCase());
}

export function mapApiDocumentToItem(doc: ApiDocument): DocumentItem {
  const uploadType = (doc.uploadType ?? "document") as DocumentUploadType;
  return {
    id: doc.id,
    name: buildDisplayName(doc),
    type: formatUploadType(uploadType),
    uploadType,
    date: doc.createdAt ?? "",
    dateLabel: formatDateLabel(doc.createdAt),
    dateShort: formatDateShort(doc.createdAt),
    sizeLabel: formatFileSize(doc.size),
    sizeBytes: Number(doc.size ?? 0),
    shipmentId: doc.listing?.id ?? "",
    kind: isPhotoUploadType(uploadType) ? "photo" : "document",
    previewUrl: doc.preview || null,
    fileUrl: doc.fileUrl || null,
    filename: doc.filename || doc.name || "document",
  };
}

import type { CustomDateRange } from "../types";

export function dateFilterToRange(
  filter: string,
  customRange?: CustomDateRange,
): { startDate?: string; endDate?: string } {
  const now = new Date();
  if (filter === "30d") {
    const start = new Date(now);
    start.setDate(start.getDate() - 30);
    return { startDate: start.toISOString() };
  }
  if (filter === "3m") {
    const start = new Date(now);
    start.setMonth(start.getMonth() - 3);
    return { startDate: start.toISOString() };
  }
  if (filter === "6m") {
    const start = new Date(now);
    start.setMonth(start.getMonth() - 6);
    return { startDate: start.toISOString() };
  }
  if (filter === "custom" && customRange) {
    const range: { startDate?: string; endDate?: string } = {};
    if (customRange.from) {
      const start = new Date(customRange.from);
      start.setHours(0, 0, 0, 0);
      range.startDate = start.toISOString();
    }
    if (customRange.to) {
      const end = new Date(customRange.to);
      end.setHours(23, 59, 59, 999);
      range.endDate = end.toISOString();
    }
    return range;
  }
  return {};
}

export function formatCustomDateRangeLabel(range?: CustomDateRange): string {
  if (!range?.from && !range?.to) return "Custom Date Range";
  const fmt = (date: Date) =>
    date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  if (range.from && range.to) return `${fmt(range.from)} – ${fmt(range.to)}`;
  if (range.from) return `From ${fmt(range.from)}`;
  if (range.to) return `Until ${fmt(range.to)}`;
  return "Custom Date Range";
}

export function listingVehicleLabel(
  vehicles?: Array<{ year?: number | string; make?: string; model?: string }>,
): string {
  const vehicle = vehicles?.[0];
  if (!vehicle) return "Shipment";
  const raw = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(" ").trim();
  return titleCaseWords(raw) || "Shipment";
}

export function listingRouteLabel(
  origin?: { city?: string; state?: string } | null,
  destination?: { city?: string; state?: string } | null,
): string {
  const from = [origin?.city, origin?.state].filter(Boolean).join(", ");
  const to = [destination?.city, destination?.state].filter(Boolean).join(", ");
  if (from && to) return `${from} to ${to}`;
  return from || to || "—";
}
